import React, { useEffect, useMemo, useState } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import FormField from '../common/FormField';
import SimpleAlert from '../common/SimpleAlert';
import Table, { TableColumn } from '../common/Table';
import FileIcons from '../common/FileIcons';
import {
  useCompetitionResultEntrySearch,
  useCompetitionResultEntryDistinctYears,
} from '../../hooks/useCompetitionResultEntryApi';
import { getScoreTemplateColumnGroups } from '../../utils/scoreTemplateColumns';
import { getMemberId } from '../../utils/tokenUtils';
import { CompetitionResultEntryDTO } from '../../services/apiClient';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';

interface RankedCompetitionResultEntryDTO extends CompetitionResultEntryDTO {
  displayRank: number;
}

interface ExerciseGroup {
  key: string;
  competitionResultId: string;
  competitionExerciseId: string;
  title: string;
  competitionName?: string;
}

const CALIBRE_ALL = '';
const DEFAULT_PAGE_SIZE = 10;

const MemberCompetitionResults: React.FC = () => {
  const memberId = getMemberId();
  const { search } = useCompetitionResultEntrySearch();
  const { getDistinctYears } = useCompetitionResultEntryDistinctYears();

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [isLoadingYears, setIsLoadingYears] = useState(true);

  const [myEntries, setMyEntries] = useState<CompetitionResultEntryDTO[]>([]);
  const [isLoadingMyEntries, setIsLoadingMyEntries] = useState(false);

  const [selectedExerciseKey, setSelectedExerciseKey] = useState<string | null>(null);
  const [leaderboardRows, setLeaderboardRows] = useState<CompetitionResultEntryDTO[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  const [calibreFilter, setCalibreFilter] = useState(CALIBRE_ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setIsLoadingYears(true);
    getDistinctYears()
      .then((years) => {
        setAvailableYears(years);
        if (years.length > 0) setSelectedYear(years[0]);
      })
      .finally(() => setIsLoadingYears(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!memberId) return;
    setIsLoadingMyEntries(true);
    search({
      page: 1,
      pageSize: 999,
      filters: { memberId },
    })
      .then((res) => setMyEntries(res.results))
      .finally(() => setIsLoadingMyEntries(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // The distinct (CompetitionResult, CompetitionExercise) pairs this member has an entry for,
  // within the selected year (derived from the competition's own StartDate, not entry creation).
  const exerciseGroups = useMemo<ExerciseGroup[]>(() => {
    if (selectedYear == null) return [];
    const map = new Map<string, ExerciseGroup>();

    myEntries.forEach((entry) => {
      const startDate = entry.competitionResult?.competition?.startDate;
      if (!startDate || new Date(startDate).getFullYear() !== selectedYear) return;
      if (!entry.competitionResultId || !entry.competitionExerciseId) return;

      const key = `${entry.competitionResultId}_${entry.competitionExerciseId}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          competitionResultId: entry.competitionResultId,
          competitionExerciseId: entry.competitionExerciseId,
          title:
            entry.competitionExercise?.exerciseSpecificTitle ||
            entry.competitionExercise?.exerciseSpecific?.title ||
            'Exercise',
          competitionName: entry.competitionResult?.competition?.name,
        });
      }
    });

    return Array.from(map.values());
  }, [myEntries, selectedYear]);

  // Default (or reset) the exercise dropdown to the first exercise whenever the available set
  // changes (e.g. switching year) and the currently selected one is no longer in it.
  useEffect(() => {
    if (exerciseGroups.length === 0) {
      setSelectedExerciseKey(null);
      return;
    }
    if (!exerciseGroups.some((g) => g.key === selectedExerciseKey)) {
      setSelectedExerciseKey(exerciseGroups[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseGroups]);

  const selectedGroup = exerciseGroups.find((g) => g.key === selectedExerciseKey) || null;

  // Load the full leaderboard (every competitor, not just this member) for the selected exercise
  // only, so the calibre filter below has a field to re-rank against.
  useEffect(() => {
    if (!selectedGroup) {
      setLeaderboardRows([]);
      return;
    }

    setCalibreFilter(CALIBRE_ALL);
    setCurrentPage(1);
    setIsLoadingLeaderboard(true);
    search({
      page: 1,
      pageSize: 999,
      sortBy: 'Position',
      sortDescending: false,
      filters: {
        competitionResultId: selectedGroup.competitionResultId,
        competitionExerciseId: selectedGroup.competitionExerciseId,
      },
    })
      .then((res) => setLeaderboardRows(res.results))
      .finally(() => setIsLoadingLeaderboard(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.key]);

  const getCalibreTitle = (entry: CompetitionResultEntryDTO): string | undefined => {
    const firearm = entry.score?.memberFirearm;
    return firearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST
      ? firearm?.caliberOther || undefined
      : firearm?.caliber?.title || undefined;
  };

  const columns = useMemo<TableColumn[]>(() => {
    const template = leaderboardRows.find((e) => e.score?.exerciseTemplate)?.score?.exerciseTemplate;
    const templateColumnGroups = template ? getScoreTemplateColumnGroups(template) : [];
    const isFiltered = !!calibreFilter;

    const base: TableColumn[] = [
      {
        label: 'Rank',
        key: 'rank',
        render: (row: RankedCompetitionResultEntryDTO) => (
          <span>{isFiltered ? row.displayRank : row.rank ?? '-'}</span>
        ),
      },
      {
        label: 'Member',
        key: 'member',
        render: (row: CompetitionResultEntryDTO) => (
          <span>
            {row.score?.member?.initials} {row.score?.member?.lastName}
            {row.score?.memberId === memberId && (
              <span className="ml-2 inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                You
              </span>
            )}
          </span>
        ),
      },
      {
        label: 'Firearm',
        key: 'firearm',
        render: (row: CompetitionResultEntryDTO) => {
          const firearm = row.score?.memberFirearm;
          const typeTitle = firearm?.firearmSpecific?.title || firearm?.firearmType?.title || '-';
          const calibreTitle =
            firearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST
              ? firearm?.caliberOther ?? '-'
              : firearm?.caliber?.title ?? '-';
          const actionTitle = firearm?.firearmAction?.title ?? '-';
          const makeTitle =
            firearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST
              ? firearm?.firearmMakeOther
              : firearm?.firearmMake?.title ?? '-';
          return (
            <div className="flex flex-col">
              <span>T: {typeTitle}</span>
              <span>C: {calibreTitle}</span>
              <span>A: {actionTitle}</span>
              <span>M: {makeTitle}</span>
            </div>
          );
        },
      },
    ];

    const dynamic: TableColumn[] = templateColumnGroups.map((group) => ({
      label: group.header,
      key: group.key,
      render: (row: CompetitionResultEntryDTO) => (
        <div className="flex flex-col">
          {group.columns.map((col) => (
            <span key={col.key}>
              {col.header}: {row.score ? col.getValue(row.score) : ''}
            </span>
          ))}
        </div>
      ),
    }));

    const targets: TableColumn[] = [
      {
        label: 'Targets',
        key: 'targets',
        render: (row: CompetitionResultEntryDTO) => {
          // Target files can show personal details (e.g. handwritten name/ID on the sheet), so a
          // member browsing this leaderboard may only view their own uploaded targets, not other
          // competitors'.
          if (row.score?.memberId !== memberId) {
            return <span className="text-gray-400">-</span>;
          }

          return (
            <FileIcons
              files={(row.score?.scoreTargetFiles || []).map((tf, i) => ({
                id: tf.fileRecordId,
                label: `Target ${i + 1}`,
              }))}
            />
          );
        },
      },
    ];

    return [...base, ...dynamic, ...targets];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboardRows, calibreFilter, memberId]);

  const calibreOptions = useMemo(
    () =>
      Array.from(
        new Set(leaderboardRows.map((row) => getCalibreTitle(row)).filter((title): title is string => !!title)),
      ).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leaderboardRows],
  );

  // Rows already come back sorted by Position/Rank ascending; when a calibre filter narrows the
  // field, re-derive a sequential rank across just those rows for on-screen display only (the
  // persisted Rank is left untouched), then paginate on top of that.
  const filteredRows = calibreFilter
    ? leaderboardRows.filter((row) => getCalibreTitle(row) === calibreFilter)
    : leaderboardRows;
  const rankedRows: RankedCompetitionResultEntryDTO[] = filteredRows.map((row, index) => ({
    ...row,
    displayRank: index + 1,
  }));
  const totalPages = Math.max(1, Math.ceil(rankedRows.length / pageSize));
  const pagedRows = rankedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (isLoadingYears || isLoadingMyEntries) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading competition results...</span>
      </div>
    );
  }

  if (availableYears.length === 0) {
    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Competition Results</h2>
        <p className="text-gray-600 dark:text-gray-400">
          No competition results found. Results appear here once your competition scores have been approved.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6 p-4">
        <h2 className="text-xl font-semibold mb-4">Competition Results</h2>
        <SimpleAlert
          variant="info"
          message="These results update live while the competition is still in progress and are not final - scores may still change as more entries are approved and ranked."
        />
        <div className="flex flex-wrap gap-2 mb-6">
          {availableYears.map((year) => (
            <Button
              key={year}
              variant={selectedYear === year ? 'primary' : 'secondary'}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </Button>
          ))}
        </div>
      </div>


      {exerciseGroups.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          No competition results found for {selectedYear}.
        </p>
      ) : (
        <Card
          title={selectedGroup?.title || 'Exercise'}
          subtitle={selectedGroup?.competitionName}
          headerActions={
            <div className="flex flex-wrap gap-2">
              <div className="w-64">
                <FormField
                  id="exercise-select"
                  label=""
                  type="dropdown"
                  value={selectedExerciseKey || ''}
                  options={exerciseGroups.map((g) => ({ value: g.key, label: g.title }))}
                  onChange={(e) => setSelectedExerciseKey(e.target.value)}
                />
              </div>
              {calibreOptions.length > 1 && (
                <div className="w-56">
                  <FormField
                    id="calibre-filter"
                    label=""
                    type="dropdown"
                    value={calibreFilter}
                    placeholder="All Calibres"
                    options={calibreOptions.map((c) => ({ value: c, label: c }))}
                    onChange={(e) => {
                      setCalibreFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </div>
          }
        >
          <Table
            columns={columns}
            data={pagedRows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            isLoading={isLoadingLeaderboard}
            noDataMessage="No results found for this exercise."
          />
        </Card>
      )}
    </div>
  );
};

export default MemberCompetitionResults;
